import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@tark/shared/components';
import {
  useAcceptCompetitionPairInvite,
  useAuth,
  useClaimCompetitionRegistration,
  useCompetitionPairInvitePreview,
  useCreateCompetitionPairInvite,
  useMyCompetitionRegistration,
  usePublicCompetition,
  usePublicCompetitionDraw,
  usePublicCompetitionDivisions,
  usePublicCompetitionResults,
  useRegisterForCompetition,
  useWebProfile,
} from '@tark/shared/hooks';
import type { CompetitionConsent, PublicCompetitionDivision } from '@tark/shared/lib';
import { CompetitionActionPanel } from '../../components/competitions/CompetitionActionPanel';
import { CompetitionDivisionList } from '../../components/competitions/CompetitionDivisionList';
import { CompetitionPublicDraw } from '../../components/competitions/CompetitionPublicDraw';
import { CompetitionPublicResults } from '../../components/competitions/CompetitionPublicResults';
import { CompetitionHandoffPanel } from '../../components/competitions/CompetitionHandoffPanel';
import {
  CompetitionOverview,
  CompetitionPublicLayout,
} from '../../components/competitions/CompetitionOverview';
import {
  CompetitionRegistrationDialog,
  type CompetitionDialogMode,
} from '../../components/competitions/CompetitionRegistrationDialog';
import {
  captureCompetitionHandoff,
  clearCompetitionHandoff,
  createCompetitionSecret,
  createPairInviteUrl,
  createSafeCompetitionReturnPath,
  shareOrCopyCompetitionLink,
} from './competitionSecretHandoff';
import {
  findDivisionEntry,
  getCompetitionActionError,
  getDivisionAvailabilityLabel,
  getEntryStatusLabel,
  isDivisionOpen,
  isFixedPairDivision,
} from './publicCompetitionUtils';

import PublicLeagueEventPage from './PublicLeagueEventPage';

export default function PublicCompetitionPage() { return <ClassicCompetitionPage />; }

function ClassicCompetitionPage() {
  const {slug=''}=useParams();const overview=usePublicCompetition(slug);
  if(overview.data?.leagueType==='DOUBLES_PILOT')return <PublicLeagueEventPage />;
  if(overview.isLoading)return <CompetitionPublicLayout accountHref="/login" accountLabel="Open TARK"><p role="status">Loading competition…</p></CompetitionPublicLayout>;
  return <ClassicCompetitionContent />;
}

function ClassicCompetitionContent() {
  const { slug = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [handoff, setHandoff] = useState(() => captureCompetitionHandoff(slug));
  const [selectedDivisionPublicId, setSelectedDivisionPublicId] = useState(
    () => searchParams.get('division') || undefined
  );
  const [dialogMode, setDialogMode] = useState<CompetitionDialogMode | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [partnerInviteUrl, setPartnerInviteUrl] = useState<string | null>(null);
  const [shareStatus, setShareStatus] = useState<string | null>(null);

  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const isAccountReady = Boolean(user && (!user.nextStep || ['TABS', 'WELCOME'].includes(user.nextStep)));
  const competitionQuery = usePublicCompetition(slug);
  const drawQuery = usePublicCompetitionDraw(slug);
  const resultsQuery = usePublicCompetitionResults(slug);
  const divisionsQuery = usePublicCompetitionDivisions(slug);
  const competition = competitionQuery.data;
  const divisions = divisionsQuery.data || [];
  const registrationQuery = useMyCompetitionRegistration(
    competition?.competitionPublicId,
    isAccountReady
  );
  const profileQuery = useWebProfile(isAccountReady);
  const pairToken = handoff?.kind === 'pair' ? handoff.token : null;
  const pairPreviewQuery = useCompetitionPairInvitePreview(pairToken, isAccountReady);
  const registerMutation = useRegisterForCompetition(competition?.competitionPublicId);
  const createPairMutation = useCreateCompetitionPairInvite(competition?.competitionPublicId);
  const acceptPairMutation = useAcceptCompetitionPairInvite();
  const claimMutation = useClaimCompetitionRegistration();

  useEffect(() => {
    if (!divisions.length) {
      return;
    }
    const requested = selectedDivisionPublicId
      ? divisions.find((division) => division.divisionPublicId === selectedDivisionPublicId)
      : undefined;
    if (requested) {
      return;
    }
    const next = divisions.find((division) =>
      competition ? isDivisionOpen(competition, division) : false
    ) || divisions[0];
    setSelectedDivisionPublicId(next.divisionPublicId);
  }, [competition, divisions, selectedDivisionPublicId]);

  const selectedDivision = useMemo(
    () => divisions.find((division) => division.divisionPublicId === selectedDivisionPublicId),
    [divisions, selectedDivisionPublicId]
  );
  const selectedEntry = selectedDivision
    ? findDivisionEntry(registrationQuery.data?.entries, selectedDivision.divisionPublicId)
    : undefined;
  const returnPath = createSafeCompetitionReturnPath(slug, selectedDivisionPublicId);
  const continueHref = isAuthenticated
    ? `/register?return=${encodeURIComponent(returnPath)}`
    : `/login?return=${encodeURIComponent(returnPath)}`;
  const headerAccountHref = isAuthenticated && isAccountReady ? '/welcome' : continueHref;
  const headerAccountLabel = isAuthLoading
    ? 'Account'
    : isAuthenticated && isAccountReady
      ? 'Open TARK'
      : isAuthenticated
        ? 'Finish setup'
        : 'Sign in';

  const pairPreviewError = pairPreviewQuery.isError
    ? getCompetitionActionError(pairPreviewQuery.error)
    : pairPreviewQuery.data && competition &&
        pairPreviewQuery.data.competitionPublicId !== competition.competitionPublicId
      ? 'This invitation belongs to a different competition.'
      : null;

  const isSubmitting =
    registerMutation.isPending ||
    createPairMutation.isPending ||
    acceptPairMutation.isPending ||
    claimMutation.isPending;

  const actionDisabled =
    !selectedDivision ||
    Boolean(selectedEntry) ||
    (competition && selectedDivision ? !isDivisionOpen(competition, selectedDivision) : true) ||
    (isAccountReady && (registrationQuery.isLoading || profileQuery.isLoading));
  const selectedStatus = competition && selectedDivision
    ? selectedEntry
      ? getEntryStatusLabel(selectedEntry)
      : getDivisionAvailabilityLabel(competition, selectedDivision)
    : undefined;
  const actionLabel = primaryActionLabel({
    isAuthenticated,
    isAccountReady,
    selectedDivision,
    selectedEntryLabel: selectedEntry ? getEntryStatusLabel(selectedEntry) : null,
    registrationAvailable: competition && selectedDivision ? isDivisionOpen(competition, selectedDivision) : false,
  });
  const handleDivisionSelect = (division: PublicCompetitionDivision) => {
    setSelectedDivisionPublicId(division.divisionPublicId);
    const next = new URLSearchParams(searchParams);
    next.set('division', division.divisionPublicId);
    setSearchParams(next, { replace: true });
    setActionError(null);
  };

  const handlePrimaryAction = () => {
    setActionError(null);
    if (!selectedDivision || !competition) {
      return;
    }
    if (!isAuthenticated || !isAccountReady) {
      window.location.href = continueHref;
      return;
    }
    if (registrationQuery.isError || profileQuery.isError) {
      setActionError('Your registration details could not be checked. Refresh and try again.');
      return;
    }
    if (selectedEntry || !isDivisionOpen(competition, selectedDivision)) {
      return;
    }
    setDialogMode(isFixedPairDivision(selectedDivision) ? 'create-pair' : 'individual');
  };

  const handleHandoffContinue = () => {
    setActionError(null);
    if (!handoff) {
      return;
    }
    if (!isAuthenticated || !isAccountReady) {
      window.location.href = continueHref;
      return;
    }
    if (handoff.kind === 'pair') {
      if (!pairPreviewQuery.data || pairPreviewError) {
        return;
      }
      setSelectedDivisionPublicId(pairPreviewQuery.data.divisionPublicId);
      setDialogMode('accept-pair');
    } else {
      setDialogMode('claim');
    }
  };

  const handleConfirm = async (consent: CompetitionConsent) => {
    if (!competition) {
      return;
    }
    setActionError(null);
    try {
      const clientMutationId = createMutationId();
      if (dialogMode === 'individual' && selectedDivision) {
        await registerMutation.mutateAsync({
          divisionPublicId: selectedDivision.divisionPublicId,
          entryType: 'INDIVIDUAL',
          partnerEntrantPublicId: null,
          clientMutationId,
          consent,
        });
        setSuccessMessage(`Registration submitted for ${selectedDivision.name}.`);
      } else if (dialogMode === 'create-pair' && selectedDivision) {
        const inviteToken = createCompetitionSecret();
        const response = await createPairMutation.mutateAsync({
          divisionPublicId: selectedDivision.divisionPublicId,
          clientMutationId,
          inviteToken,
          consent,
        });
        setPartnerInviteUrl(createPairInviteUrl(slug, response.inviteToken || inviteToken));
        setSuccessMessage(null);
      } else if (dialogMode === 'accept-pair' && pairToken) {
        await acceptPairMutation.mutateAsync({ inviteToken: pairToken, clientMutationId, consent });
        clearCompetitionHandoff(slug, 'pair');
        setHandoff(null);
        setSuccessMessage('Your doubles team is registered.');
      } else if (dialogMode === 'claim' && handoff?.kind === 'claim') {
        await claimMutation.mutateAsync({ token: handoff.token, clientMutationId, consent });
        clearCompetitionHandoff(slug, 'claim');
        setHandoff(null);
        setSuccessMessage('Your registration is now linked to this TARK account.');
      }
      setDialogMode(null);
    } catch (error) {
      setActionError(getCompetitionActionError(error));
    }
  };

  const handleSharePartnerLink = async () => {
    if (!partnerInviteUrl || !competition) {
      return;
    }
    try {
      const result = await shareOrCopyCompetitionLink(partnerInviteUrl, competition.name);
      setShareStatus(result === 'copied' ? 'Link copied' : 'Share opened');
    } catch {
      setShareStatus('Could not copy the link');
    }
  };

  if (competitionQuery.isLoading || divisionsQuery.isLoading) {
    return (
      <CompetitionPublicLayout accountHref={headerAccountHref} accountLabel={headerAccountLabel}>
        <div className="flex min-h-[55vh] items-center justify-center gap-2 text-sm font-semibold text-text-muted">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          Loading competition
        </div>
      </CompetitionPublicLayout>
    );
  }

  if (competitionQuery.isError || !competition) {
    return (
      <CompetitionPublicLayout accountHref={headerAccountHref} accountLabel={headerAccountLabel}>
        <div className="mx-auto max-w-lg py-20 text-center">
          <AlertCircle className="mx-auto h-9 w-9 text-text-subtle" />
          <h1 className="mt-4 text-2xl font-bold text-text">Competition unavailable</h1>
          <p className="mt-2 text-sm text-text-muted">Check the shared link or ask the organizer for a new one.</p>
          <Button className="mt-6" variant="outline" onClick={() => competitionQuery.refetch()}>
            Try again
          </Button>
        </div>
      </CompetitionPublicLayout>
    );
  }

  if (divisionsQuery.isError) {
    return (
      <CompetitionPublicLayout accountHref={headerAccountHref} accountLabel={headerAccountLabel}>
        <CompetitionOverview competition={competition} />
        <div className="mx-auto max-w-lg py-14 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-text-subtle" />
          <h2 className="mt-3 text-xl font-bold text-text">Divisions unavailable</h2>
          <p className="mt-2 text-sm text-text-muted">The division list could not be loaded.</p>
          <Button className="mt-5" variant="outline" onClick={() => divisionsQuery.refetch()}>
            Try again
          </Button>
        </div>
      </CompetitionPublicLayout>
    );
  }
  return (
    <CompetitionPublicLayout accountHref={headerAccountHref} accountLabel={headerAccountLabel}>
      <CompetitionOverview competition={competition} />

      {handoff ? (
        <CompetitionHandoffPanel
          handoff={handoff}
          isAuthenticated={isAuthenticated}
          isAccountReady={isAccountReady}
          pairPreview={pairPreviewQuery.data}
          isPreviewLoading={pairPreviewQuery.isLoading}
          previewError={pairPreviewError}
          accountHref={continueHref}
          onContinue={handleHandoffContinue}
        />
      ) : null}

      {successMessage ? (
        <div role="status" className="mt-6 flex items-start gap-3 rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-700">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          {successMessage}
        </div>
      ) : null}

      <div className="mt-8 grid gap-7 pb-24 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start lg:pb-0">
        <CompetitionDivisionList
          competition={competition}
          divisions={divisions}
          entries={registrationQuery.data?.entries}
          selectedDivisionPublicId={selectedDivisionPublicId}
          onSelect={handleDivisionSelect}
        />

        <CompetitionActionPanel
          selectedName={selectedDivision?.name || 'Choose a division'}
          selectedStatus={selectedStatus}
          actionLabel={actionLabel}
          actionDisabled={actionDisabled}
          isChecking={registrationQuery.isLoading && isAccountReady}
          partnerInviteReady={Boolean(partnerInviteUrl)}
          shareStatus={shareStatus}
          error={actionError}
          onAction={handlePrimaryAction}
          onShare={handleSharePartnerLink}
        />
      </div>

      {drawQuery.data?.status === 'PUBLISHED' && drawQuery.data.divisions.length > 0 ? (
        <CompetitionPublicDraw divisions={drawQuery.data.divisions} />
      ) : null}

      {resultsQuery.data && (resultsQuery.data.divisions.some((division) => division.standings.length > 0)
        || resultsQuery.data.matches.length > 0) ? (
          <CompetitionPublicResults data={resultsQuery.data} />
        ) : null}

      <CompetitionActionPanel
        mobile
        selectedName={selectedDivision?.name || 'Choose a division'}
        selectedStatus={selectedStatus}
        actionLabel={actionLabel}
        actionDisabled={actionDisabled}
        isChecking={registrationQuery.isLoading && isAccountReady}
        partnerInviteReady={Boolean(partnerInviteUrl)}
        shareStatus={shareStatus}
        error={actionError}
        onAction={handlePrimaryAction}
        onShare={handleSharePartnerLink}
      />
      <CompetitionRegistrationDialog
        open={Boolean(dialogMode)}
        mode={dialogMode || 'individual'}
        competition={competition}
        division={
          dialogMode === 'accept-pair'
            ? divisions.find((division) => division.divisionPublicId === pairPreviewQuery.data?.divisionPublicId)
            : selectedDivision
        }
        profile={profileQuery.data}
        pairPreview={pairPreviewQuery.data}
        isSubmitting={isSubmitting}
        error={actionError}
        onClose={() => {
          if (!isSubmitting) {
            setDialogMode(null);
            setActionError(null);
          }
        }}
        onConfirm={handleConfirm}
      />
    </CompetitionPublicLayout>
  );
}

function createMutationId(): string {
  if (typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (char) =>
    (Number(char) ^ (window.crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(char) / 4)))).toString(16)
  );
}

function primaryActionLabel({
  isAuthenticated,
  isAccountReady,
  selectedDivision,
  selectedEntryLabel,
  registrationAvailable,
}: {
  isAuthenticated: boolean;
  isAccountReady: boolean;
  selectedDivision?: PublicCompetitionDivision;
  selectedEntryLabel: string | null;
  registrationAvailable: boolean;
}): string {
  if (!selectedDivision) {
    return 'Choose a division';
  }
  if (selectedEntryLabel) {
    return selectedEntryLabel;
  }
  if (!registrationAvailable) {
    return 'Unavailable';
  }
  if (!isAuthenticated) {
    return 'Sign in to register';
  }
  if (!isAccountReady) {
    return 'Finish account setup';
  }
  return isFixedPairDivision(selectedDivision) ? 'Create partner link' : 'Register';
}
